import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Spin, message } from "antd";
import { Paperclip } from "@phosphor-icons/react";
import {
  getWaitingOnWorkItems,
  attachDocumentToWorkItem,
} from "../../service/inbox";
import { toInstructionHtml } from "./CreateWorkItemModal";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";

/**
 * Attach to Work Item — the conveyor belt's return move.
 *
 * Matthew sends Anne "Get statement — migraines"; the statement arrives
 * by email or mail into this inbox. Whoever is reading it picks the open
 * work item it answers, adds a word if they want, and confirms. The
 * document link lands in the item's thread, and a Sent item volleys
 * straight back to the requester's court with one bell — no separate
 * "now go tell Matthew" step.
 *
 * The picker is the matter's open (Sent) work items from the waiting-on
 * endpoint — the same list the banner shows. `initialWorkItemId` lets
 * the banner's per-item Attach button arrive preselected; the footer's
 * generic button starts blank.
 */
const AttachToWorkItemModal = ({
  open,
  documentId,
  initialWorkItemId = null,
  onClose,
  onAttached = undefined,
}) => {
  const [loadStatus, setLoadStatus] = useState("idle");
  const [items, setItems] = useState([]);
  const [workItemId, setWorkItemId] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let stale = false;
    setWorkItemId(initialWorkItemId || null);
    setNote("");
    setError(null);
    setLoadStatus("loading");
    getWaitingOnWorkItems(documentId)
      .then((response) => {
        if (stale) return;
        const data = response?.data || {};
        setItems(data.items || []);
        setLoadStatus("ready");
      })
      .catch(() => {
        if (stale) return;
        setLoadStatus("failed");
        setError(
          "Couldn't reach the new Salesforce to load this matter's open work items."
        );
      });
    return () => {
      // The reader can switch documents with the modal mounted; the
      // picker must never show the previous matter's items (Bugbot, #634).
      stale = true;
    };
  }, [open, documentId, initialWorkItemId]);

  const options = useMemo(
    () =>
      items.map((item) => ({
        value: item.workItemId,
        label: `${item.itemNumber} — ${item.subject}${
          item.holderName ? ` (with ${item.holderName})` : ""
        }`,
      })),
    [items]
  );

  const selected = useMemo(
    () => items.find((item) => item.workItemId === workItemId) || null,
    [items, workItemId]
  );

  const canSubmit =
    loadStatus === "ready" && workItemId != null && !submitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const trimmedNote = note.trim();
    attachDocumentToWorkItem(documentId, {
      work_item_id: workItemId,
      ...(trimmedNote ? { note: toInstructionHtml(trimmedNote) } : {}),
    })
      .then((response) => {
        const volleyed = Boolean(response?.data?.volleyedToRequester);
        const number = selected?.itemNumber;
        const requester = selected?.requestedByName;
        message.success(
          volleyed
            ? `Attached — ${number || "the work item"} is back in ${
                requester ? `${requester}'s` : "the requester's"
              } court.`
            : `Attached to ${number || "the work item"}.`
        );
        if (onAttached) onAttached();
        onClose();
      })
      .catch((err) => {
        setError(
          err?.response?.data?.detail ||
            "Couldn't attach the document. Please try again."
        );
      })
      .finally(() => setSubmitting(false));
  }, [canSubmit, documentId, workItemId, note, selected, onAttached, onClose]);

  const title = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Paperclip size={16} weight="bold" />
      Attach to Work Item
    </span>
  );

  const nothingWaiting = loadStatus === "ready" && items.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Attach & return"
      okButtonProps={{
        disabled: !canSubmit,
        loading: submitting,
        style: nothingWaiting ? { display: "none" } : undefined,
      }}
      cancelText={nothingWaiting ? "Close" : "Cancel"}
      title={title}
      destroyOnClose
    >
      {loadStatus === "loading" && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
          <InboxModalHint style={{ marginTop: 8 }}>
            Loading this matter's open work items…
          </InboxModalHint>
        </div>
      )}

      {nothingWaiting && (
        <InboxModalHint>
          This matter has no open work items waiting on anything, so
          there's nothing to attach this document to.
        </InboxModalHint>
      )}

      {loadStatus === "ready" && items.length > 0 && (
        <>
          <InboxModalHint style={{ marginBottom: 12 }}>
            The document link lands in the work item's thread, and the item
            goes back to whoever asked for it — one step.
          </InboxModalHint>

          <InboxModalFieldGroup>
            <InboxModalLabel htmlFor="attach-work-item">
              Work item
            </InboxModalLabel>
            <Select
              id="attach-work-item"
              style={{ width: "100%" }}
              placeholder="Which work item does this answer?"
              options={options}
              value={workItemId}
              onChange={setWorkItemId}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </InboxModalFieldGroup>

          <InboxModalFieldGroup>
            <InboxModalLabel htmlFor="attach-note">
              Note (optional)
            </InboxModalLabel>
            <Input.TextArea
              id="attach-note"
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Came in this morning's mail — looks complete."
              maxLength={4000}
            />
          </InboxModalFieldGroup>
        </>
      )}

      {error && (
        <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>
      )}
    </Modal>
  );
};

AttachToWorkItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialWorkItemId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAttached: PropTypes.func,
};

export default AttachToWorkItemModal;
